import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";

const NAV = [
  { href: "/#how-it-works", label: "How It Works" },
  { href: "/#pricing", label: "Pricing" },
];

/* ============================================================
 *  SiteHeader — the one shared public header (NOT sticky).
 *  Official transparent TPWH logo + slogan artwork, height-only
 *  sizing (aspect ratios preserved). Compact single row on
 *  desktop; wraps to two rows on small screens.
 * ============================================================ */
export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  return (
    <motion.header
      className="relative z-40"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
    >
      <div className="bg-gradient-to-r from-[#0E2F5F]/70 via-[#0D1421] to-[#010204]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          {/* Brand unit: logo + slogan, artwork size preserved (height-only) */}
          <a
            href="/"
            className="flex items-center gap-3 sm:gap-5 group min-w-0"
            data-testid="brand-logo"
          >
            <img
              src="/brand/TPWH_Logo_Transparent.png"
              alt="The Pros Work Hub"
              className="h-[100px] sm:h-[120px] lg:h-[150px] w-auto shrink-0"
              data-testid="brand-logo-img"
            />
            <img
              src="/brand/TPWH_Slogan_Transparent.png"
              alt="Built by a Pro for Pro's and Tradesmen alike."
              className="h-[86px] sm:h-[100px] lg:h-[126px] w-auto shrink-0"
              data-testid="brand-slogan"
            />
          </a>

          {/* Nav — exactly four items: How It Works, Pricing, Login, Post a Work Request */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <nav className="hidden lg:flex items-center gap-8 text-[13px] font-medium text-[#C9C6C6] uppercase tracking-[0.15em]">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  className="link-sweep hover:text-[#0E93EC] transition-colors"
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </a>
              ))}
            </nav>

            {/* Login dropdown — works by tap on every screen size */}
            <div className="relative">
              <button
                onClick={() => setLoginOpen((v) => !v)}
                className="text-[12px] sm:text-[13px] uppercase tracking-[0.15em] font-medium text-[#C9C6C6] hover:text-[#0E93EC] px-2.5 py-2 inline-flex items-center gap-1"
                data-testid="header-login-toggle"
                aria-haspopup="menu"
                aria-expanded={loginOpen}
              >
                Login <ChevronDown className={`w-3.5 h-3.5 transition-transform ${loginOpen ? "rotate-180" : ""}`} />
              </button>
              {loginOpen && (
                <div
                  className="absolute right-0 top-full mt-2 min-w-[200px] bg-[#0D1421] border border-[#0E93EC]/25 shadow-xl py-1 z-50"
                  data-testid="header-login-dropdown"
                >
                  <Link
                    to="/homeowner/login"
                    className="block px-4 py-2.5 text-sm text-[#C9C6C6] hover:bg-[#0E2F5F] hover:text-[#EEEEEF]"
                    data-testid="header-homeowner-signin"
                    onClick={() => setLoginOpen(false)}
                  >
                    Homeowner Login
                  </Link>
                  <Link
                    to="/pro/login"
                    className="block px-4 py-2.5 text-sm text-[#C9C6C6] hover:bg-[#0E2F5F] hover:text-[#EEEEEF]"
                    data-testid="header-signin"
                    onClick={() => setLoginOpen(false)}
                  >
                    Pro Login
                  </Link>
                </div>
              )}
            </div>

            <a
              href="/#guest"
              className="hidden sm:inline-flex items-center bg-[#0E93EC] text-white hover:bg-[#0E2F5F] rounded-none px-5 h-10 font-medium text-sm transition-colors"
              data-testid="header-post-request"
            >
              Post a Work Request
            </a>

            <button
              className="lg:hidden p-2 text-[#EEEEEF]"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Blue glow line */}
        <div className="h-[3px] bg-[#0E93EC] shadow-[0_0_18px_rgba(14,147,236,0.75)]" />

        {/* Mobile menu — same four items */}
        {open && (
          <div className="lg:hidden border-t border-[#0E93EC]/20 bg-[#0D1421] px-6 py-6 space-y-4">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="block text-[#C9C6C6] uppercase tracking-[0.15em] text-sm"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </a>
            ))}
            <div className="pt-2 space-y-2">
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C9C6C6]/60">
                Login
              </div>
              <Link
                to="/homeowner/login"
                onClick={() => setOpen(false)}
                className="block pl-3 text-[13px] uppercase tracking-[0.15em] font-medium text-[#C9C6C6]"
                data-testid="mobile-homeowner-signin"
              >
                Homeowner Login
              </Link>
              <Link
                to="/pro/login"
                onClick={() => setOpen(false)}
                className="block pl-3 text-[13px] uppercase tracking-[0.15em] font-medium text-[#C9C6C6]"
                data-testid="mobile-pro-signin"
              >
                Pro Login
              </Link>
            </div>
            <a
              href="/#guest"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none w-full h-12 font-medium"
              data-testid="mobile-post-request"
            >
              Post a Work Request
            </a>
          </div>
        )}
      </div>
    </motion.header>
  );
}
